import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Buildings, Gauge, Users as UsersIcon, CalendarDots, BellRinging, SignOut, CalendarBlank, Notepad, UsersThree, Receipt, LockKey, ClipboardText, Coins, FileText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const nav = [
  { to: "/", label: "Dashboard", icon: Gauge, testid: "nav-dashboard" },
  { to: "/clients", label: "Company", icon: UsersIcon, testid: "nav-clients" },
  { to: "/companies-by-fye", label: "Company by FYE", icon: CalendarBlank, testid: "nav-by-fye" },
  { to: "/templates", label: "Resolutions", icon: Notepad, testid: "nav-templates" },
  { to: "/calendar", label: "Calendar", icon: CalendarDots, testid: "nav-calendar" },
  { to: "/reminders", label: "Reminders", icon: BellRinging, testid: "nav-reminders" },
  { to: "/audit", label: "Audit", icon: ClipboardText, testid: "nav-audit" },
  { to: "/tax", label: "Tax", icon: Receipt, testid: "nav-tax" },
  { to: "/users", label: "User Maintenance", icon: UsersThree, testid: "nav-users", adminOnly: true },
  { to: "/access-tokens", label: "Access Tokens", icon: Coins, testid: "nav-access-tokens", adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-[#F3F4F6]">
      {/* Blue top bar */}
      <header className="h-16 bg-[#1E6BC6] flex items-center px-6 shrink-0">
        <span className="font-semibold text-white tracking-wide">Company Secretary</span>
        <div className="ml-auto text-white/90 text-sm truncate" title={user?.email}>{user?.email}</div>
      </header>

      <div className="flex-1 grid grid-cols-[240px_1fr]">
        <aside className="bg-white border-r border-[#E5E7EB] flex flex-col">
          <div className="h-14 bg-[#1E6BC6] px-6 flex items-center">
            <span className="font-mono uppercase tracking-[0.2em] text-sm text-white font-semibold">Menu</span>
          </div>

          <nav className="flex-1 p-2 space-y-0.5">
            {nav.filter(n => !n.adminOnly || user?.role === "admin").map(({ to, label, icon: Icon, testid }) => (
              <NavLink key={to} to={to} end={to === "/"}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-colors
                   ${isActive
                      ? "bg-[#E7F0FA] text-[#1E6BC6] font-medium"
                      : "text-[#1F2937] hover:bg-[#F3F4F6]"}`
                }>
                <Icon size={18} weight="duotone" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="p-3 border-t border-[#E5E7EB]">
            <ChangePasswordButton />
            <Button variant="outline" onClick={() => { logout(); navigate("/login"); }}
                    className="w-full rounded-md h-9 justify-start mt-2" data-testid="logout-btn">
              <SignOut size={16} weight="bold" className="mr-2" /> Sign out
            </Button>
          </div>
        </aside>

        <main className="min-w-0 bg-[#F3F4F6]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function ChangePasswordButton() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!current) return toast.error("Enter your current password");
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setOpen(false); setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to change password");
    } finally { setLoading(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}
              className="w-full rounded-sm h-9 justify-start" data-testid="change-password-btn">
        <LockKey size={16} weight="bold" className="mr-2" /> Change password
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm rounded-sm">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>Enter your current password, then set a new one (min. 6 characters).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Current password</Label>
              <Input className="mt-1 rounded-sm h-10" type="password" value={current}
                     onChange={e => setCurrent(e.target.value)} data-testid="change-pwd-current-input" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">New password</Label>
              <Input className="mt-1 rounded-sm h-10" type="password" value={next}
                     onChange={e => setNext(e.target.value)} data-testid="change-pwd-new-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Min. 6 characters. Must differ from current password.</p>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Confirm new password</Label>
              <Input className="mt-1 rounded-sm h-10" type="password" value={confirm}
                     onChange={e => setConfirm(e.target.value)} data-testid="change-pwd-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={submit} disabled={loading}
                    data-testid="change-pwd-submit-btn">
              {loading ? "Saving…" : "Update password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
